import { Button, Input, Select, Message, currencyToNumber } from '@iwoca/orion';
import { useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import { RootState } from '../../../../../iwProvider/configureStore';
import messages from './rblTopUpForm.messages';
import {
    FormattedMessage,
    injectIntl,
    WrappedComponentProps
} from 'react-intl';
import { reasons } from './rblTopUpReasons';
import styles from './rblTopUpForm.module.css';
import { useQuery } from '@tanstack/react-query';
import {
    fetchGetProductOffers,
    PostApplicationRequestBody,
    fetchPutState,
    fetchPostApplication,
    fetchGetStateByStateKey
} from '@iwoca/lapi-client/edge';
import ApprovalRequirements from '../../../../applyFlexiLoanView/applyFlexiLoanFormView/ApprovalRequirements/ApprovalRequirements';
import { getDocumentRequirementsUsingProductOffers } from '../../../../multiLoanDashboard/multiLoanDashboardSelector';
import { hasProductDocsComplete } from '../../../../../api/lendingAPI/ProductOfferAPIHelper';
import { REVENUE_BASED_LOAN_PRODUCT_TYPE } from '../../../../multiLoanDashboard/accountsSelector';
import * as selectors from '../../../../multiLoanDashboard/accountsSelector';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';

const mapStateToProp = (state: RootState) => {
    const productType = selectors.getProductTypeFromURL();
    return {
        stateKey:
            state.siteContext.impersonatingStateKey ||
            state.siteContext.state_key,
        currentPrincipal: selectors.getOutstandingPrincipal(state, productType),
        currentCreditLimit: selectors.getCreditLimit(state, productType)
    };
};

type RBLFormProps = ReturnType<typeof mapStateToProp> & WrappedComponentProps;

type FormValues = {
    turnover: number;
    credit: number;
    reason: string;
    detailedReason?: string;
};

// Todo: Make this value dynamic based on BE. Value is currently not exposed via the LAPI
const MAX_REQUEST_AMOUNT = 500000;

const RblTopUpForm = ({ intl, stateKey }: RBLFormProps) => {
    const [docRequirements, setDocRequirements] =
        useState<
            ReturnType<typeof getDocumentRequirementsUsingProductOffers>
        >();
    const { data: customerState } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey: stateKey! }),
        staleTime: Infinity
    });

    const { data: accountsData } = useQuery(getAccountsQueryConfig(stateKey!));

    const currentPrincipal = useMemo(() => {
        const account = accountsData?.data?.accounts?.find(
            (acc) => acc.product_type === 'merchant_cash_advance'
        );
        return account?.balance?.principal || 0;
    }, [accountsData]);

    const currentCreditLimit = useMemo(() => {
        const account = accountsData?.data?.accounts?.find(
            (acc) => acc.product_type === 'merchant_cash_advance'
        );
        return account?.max_approved_loan_amount || 0;
    }, [accountsData]);

    const maxRequestAmountMinusCurrentPrincipal =
        MAX_REQUEST_AMOUNT - currentPrincipal;

    const {
        register,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
        watch,
        setError
    } = useForm<FormValues>({
        mode: 'onChange'
    });

    const currency = {
        locale: intl.locale,
        currencySymbol: true,
        minorUnits: false
    };

    const {
        handleSubmit: submitDocs,
        formState: { isSubmitSuccessful }
    } = useForm();
    const watchReason = watch('reason');
    const watchCredit = watch('credit');

    const onSubmit: SubmitHandler<FormValues> = async (formData) => {
        const newCustomerState = JSON.parse(JSON.stringify(customerState));
        newCustomerState.data.application.company.last_12_months_turnover = {
            amount: formData.turnover,
            datetime: new Date().toISOString()
        };

        const combinedPrincipalAndRequestedAmount =
            formData.credit + currentPrincipal;

        const application = {
            data: {
                requested_products: [
                    {
                        amount: combinedPrincipalAndRequestedAmount,
                        requested_cash_amount: formData.credit,
                        purpose: formData.reason,
                        detailed_purpose:
                            formData.reason === 'other'
                                ? formData.detailedReason
                                : undefined,
                        product_type: REVENUE_BASED_LOAN_PRODUCT_TYPE,
                        duration: {
                            amount: 12,
                            unit: 'months'
                        }
                    }
                ]
            }
        } as PostApplicationRequestBody;

        try {
            await fetchPutState({
                stateKey: stateKey!,
                body: newCustomerState
            });

            await fetchPostApplication({
                stateKey: stateKey!,
                body: application
            });

            const productOffers = await fetchGetProductOffers({
                stateKey: stateKey!
            });

            if (productOffers?.data) {
                const product = productOffers.data.products.find(
                    (product) => product.is_suggested
                )!;
                if (!hasProductDocsComplete(product)) {
                    return setDocRequirements(
                        getDocumentRequirementsUsingProductOffers(product!)
                    );
                }
                return window.location.reload();
            }
        } catch (err) {
            // TODO: error is unsed
            setError('turnover', {
                type: 'apiError',
                message: `${err}`
            });
        }
    };

    const creditInfo =
        watchCredit > 0 &&
        watchCredit <= maxRequestAmountMinusCurrentPrincipal &&
        // If new credit limit (inputted amount + currentPrincipal) is more than current
        // credit limit show new credit limit message, else show current credit limit message
        (watchCredit + currentPrincipal > currentCreditLimit ? (
            <Message
                icon="infoCircleFilled"
                message={`${intl.formatMessage(
                    messages.newCreditLimit
                )} ${intlFormatCurrency({
                    intl: intl,
                    value: watchCredit + currentPrincipal
                })}`}
            />
        ) : (
            <Message
                icon="infoCircleFilled"
                message={`${intl.formatMessage(
                    messages.unchangedCreditLimit
                )} ${intlFormatCurrency({
                    intl: intl,
                    value: currentCreditLimit,
                    decimalPlaces: 2
                })}`}
            />
        ));

    return (
        <div className={styles.formWrapper}>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <h1>
                    <FormattedMessage {...messages.title} />
                </h1>

                <div className={styles.creditWrapper}>
                    <Input
                        {...register('credit', {
                            setValueAs: (val) => {
                                return val
                                    ? currencyToNumber(val, currency)
                                    : undefined;
                            },
                            required: intl.formatMessage(messages.creditError),
                            max: {
                                value: maxRequestAmountMinusCurrentPrincipal,
                                message: intl.formatMessage(
                                    messages.maxCreditError,
                                    {
                                        maxAmount: intlFormatCurrency({
                                            intl: intl,
                                            value: maxRequestAmountMinusCurrentPrincipal
                                        })
                                    }
                                )
                            }
                        })}
                        id="credit"
                        label={intl.formatMessage(messages.creditQuestion)}
                        type="currency"
                        currency={currency}
                        placeholder={intlFormatCurrency({
                            intl: intl,
                            value: 10000
                        })}
                        error={errors.credit?.message}
                    />
                    <div className={styles.creditInfo}>{creditInfo}</div>
                </div>

                <div>
                    <Input
                        {...register('turnover', {
                            setValueAs: (val) => {
                                return val
                                    ? currencyToNumber(val, currency)
                                    : undefined;
                            },
                            required: intl.formatMessage(messages.turnoverError)
                        })}
                        label={intl.formatMessage(messages.turnoverQuestion)}
                        id="turnover"
                        type="currency"
                        currency={currency}
                        placeholder={intlFormatCurrency({
                            intl: intl,
                            value: 100000
                        })}
                        error={
                            errors.turnover &&
                            (errors.turnover.message as string)
                        }
                    />
                </div>

                <div>
                    <Select
                        {...register('reason', {
                            required: intl.formatMessage(messages.reasonError)
                        })}
                        id="reason"
                        label={intl.formatMessage(messages.reasonQuestion)}
                        placeholder={intl.formatMessage(
                            messages.reasonPlaceholder
                        )}
                        error={
                            errors.reason && (errors.reason.message as string)
                        }
                    >
                        {reasons(intl)
                            .filter((reason) => reason.label !== ' ')
                            .map((reason, index) => (
                                <option key={index} value={reason.value}>
                                    {reason.label}
                                </option>
                            ))}
                    </Select>
                </div>

                {watchReason === 'other' && (
                    <div>
                        <Input
                            {...register('detailedReason', {
                                required: intl.formatMessage(
                                    messages.detailedReasonError
                                )
                            })}
                            id="detailedReason"
                            label={intl.formatMessage(messages.detailedReason)}
                            error={
                                errors.detailedReason &&
                                (errors.detailedReason.message as string)
                            }
                        />
                    </div>
                )}
                {!docRequirements && (
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={!isValid}
                        loading={isSubmitting}
                    >
                        <FormattedMessage {...messages.nextButton} />
                    </Button>
                )}
            </form>
            {docRequirements && docRequirements.length !== 0 ? (
                <form
                    className={styles.approvalRequirements}
                    onSubmit={submitDocs(async () => {
                        const productOffers = await fetchGetProductOffers({
                            stateKey: stateKey!
                        });

                        if (productOffers?.data) {
                            const product = productOffers.data.products.find(
                                (product) => product.is_suggested
                            )!;
                            if (hasProductDocsComplete(product))
                                window.location.reload();
                        }
                    })}
                >
                    <ApprovalRequirements
                        submitFailed={!isSubmitSuccessful}
                        documentRequirements={docRequirements}
                    />
                </form>
            ) : null}
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProp)(injectIntl(RblTopUpForm));
